import styled from "@emotion/styled"
import { easeInOut } from "@porsche-design-system/components-react/emotion"
import { useCallback, useRef, useState } from "react"
import { Stage } from "../stage/Stage"
import type { StageProps } from "../stage/Stage"
import { ANIMATION_DURATION } from "./animationDuration"
import { observeResize } from "./sharedResizeObserver"

type AnimatedStageProps = Omit<StageProps, "className">

export const AnimatedStage = ({ children, ...rest }: AnimatedStageProps) => {
	const [height, setHeight] = useState<string>("auto")
	const [isAnimating, setIsAnimating] = useState(false)
	const disposeObserverRef = useRef<(() => void) | null>(null)
	const timeoutRef = useRef<ReturnType<typeof setTimeout>>(null)
	const rafRef = useRef<number>(null)

	// note: `interpolate-size`/`calc-size(auto)` can't replace this - they only animate a discrete change of the
	// `height` property's specified value (e.g. toggling between a length and `auto`), not a continuous resize while
	// `height` stays `auto` the whole time, which is what happens here as the content grows/shrinks
	// uses a single shared ResizeObserver instance (see sharedResizeObserver.ts) instead of one per AnimatedStage,
	// so that many instances resizing/unmounting at once (e.g. during route navigation) don't trigger
	// "ResizeObserver loop completed with undelivered notifications"
	const containerRef = useCallback((node: HTMLDivElement) => {
		if (node) {
			disposeObserverRef.current = observeResize(node, (entry) => {
				if (rafRef.current) {
					cancelAnimationFrame(rafRef.current)
				}
				rafRef.current = requestAnimationFrame(() => {
					if (timeoutRef.current) {
						clearTimeout(timeoutRef.current)
					}
					setIsAnimating(true)
					setHeight(`${entry.contentRect.height}px`)

					timeoutRef.current = setTimeout(() => setIsAnimating(false), ANIMATION_DURATION)
				})
			})
		} else if (disposeObserverRef.current) {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current)
			}
			if (rafRef.current) {
				cancelAnimationFrame(rafRef.current)
			}
			disposeObserverRef.current()
			disposeObserverRef.current = null
		}
	}, [])

	return (
		<StyledStage $animating={isAnimating} $height={height} {...rest}>
			<div ref={containerRef}>{children}</div>
		</StyledStage>
	)
}

const StyledStage = styled(Stage)<{
	$animating: boolean
	$height: string
}>`
	transition: height ${ANIMATION_DURATION}ms ${easeInOut};
	height: ${({ $height }) => $height};
	overflow: ${({ $animating }) => ($animating ? "hidden" : "initial")};
`
